import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import {
  GetEmailDocument,
  GetEmailDocumentList,
} from "../../api/downstreamSummaryApi/downstreamSummaryApi";
import { useSnackbar } from "../Common/SnackbarContext";
import LocalStorageUtil from "../../utils/localStorage";

interface EmailDocumentProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  selectedData: any;
}

const EmailDocument: React.FC<EmailDocumentProps> = ({
  isOpen,
  onClose,
  title,
  selectedData,
}) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [emailDocumentData, setEmailDocumentData] = useState<any>(null);
  const [toEmail, setToEmail] = useState<string>("");
  const [toEmailError, setToEmailError] = useState<string>("");
  const [ccEmail, setCCEmail] = useState<string>("");
  const [ccEmailError, setCCEmailError] = useState<string>("");
  const [subject, setSubject] = useState<string>("");
  const [subjectError, setSubjectError] = useState<string>("");
  const emailRegex = /^(?:[a-zA-Z0-9._%+-]+)@(?:[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);

  //To Email validation on text change
  const handleToEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = event.target.value;
      setToEmail(value);
      if (!emailRegex.test(value)) {
        setToEmailError("Invalid To email address");
      } else {
        setToEmailError("");
      }
    } catch (error) {
      console.error("Error in To Email", error);
      throw error;
    }
  };

  //CC Email validation on text change
  const handleCCEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = event.target.value;
      setCCEmail(value);
      if (!emailRegex.test(value)) {
        setCCEmailError("Invalid CC email address");
      } else {
        setCCEmailError("");
      }
    } catch (error) {
      console.error("Error in CC Email", error);
      throw error;
    }
  };

  //Subject validation on text change
  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = event.target.value;
      setSubject(value);
      if (value === "") {
        setSubjectError("Subject cannot be empty");
      } else {
        setSubjectError("");
      }
    } catch (error) {
      console.error("Error in Subject", error);
      throw error;
    }
  };

  //Call the getEmailDocumentData function when the dialog box is opened
  useEffect(() => {
    if (isOpen) {
      getEmailDocumentData();
    }
  }, [isOpen]);

  //Fetch the email document data from the API
  const getEmailDocumentData = async () => {
    try {
      setLoading(true);
      const response = await GetEmailDocumentList(selectedData);
      setEmailDocumentData(response);
    } catch (error) {
      console.error("Error in Email Document fetching", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  //Close the dialog box
  const handleCloseClick = () => {
    setConfirmDialogOpen(true);
  };

  //Close the dialog box after confirmation
  const handleConfirmDialogClose = (confirm: boolean) => {
    setConfirmDialogOpen(false);
    if (confirm) {
      onClose();
    }
  };

  //Send the email
  const handelSendEmail = async () => {
    try {
      setLoading(true);
      if (toEmail === "") {
        setToEmailError("To Email cannot be empty");
        return;
      } else if (subject === "") {
        setSubjectError("Subject cannot be empty");
        return;
      } else if (!emailRegex.test(toEmail)) {
        setToEmailError("Invalid To email address");
        return;
      } else {
        const emailData = {
          contextName: LocalStorageUtil.getItem("contextName")?.toString(),
          accountId: LocalStorageUtil.getItem("accountId")?.toString(),
          DocumentLink: emailDocumentData[0],
          Recipients: toEmail,
          CCEmail: ccEmail,
          Subject: subject,
        };

        const response = await GetEmailDocument(emailData);
        if (response) {
          showSnackbar("Email Sent Successfully.");
          onClose();
        } else {
          showSnackbar("Email Sent Failed.");
        }
      }
    } catch (error) {
      console.log("Error in Email Document fetching", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={handleCloseClick}
        maxWidth="xs"
        PaperProps={{
          style: {
            height: "70%",
            minWidth: "10%",
          },
        }}
      >
        <DialogTitle
          style={{
            backgroundColor: "var(--primary-color)",
            color: "var(--aggrid-text-color)",
            fontSize: "16px",
            fontWeight: 500,
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            className="col-sm-12"
            style={{
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
              height: "3em",
            }}
          >
            <div
              className="col-sm-11"
              style={{
                display: "flex",
                alignItems: "left",
                gap: "10px",
                color: "var(--aggrid-text-color)",
                fontSize: "1em",
              }}
            >
              {title}
            </div>
            <div
              className="col-sm-1"
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "right",
              }}
            >
              {" "}
              <Tooltip title="Close">
                <IconButton
                  onClick={onClose}
                  style={{
                    cursor: "pointer",
                    alignItems: "flex-end",
                    color: "var(--aggrid-text-color)",
                    width: "20px",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "60%",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <div className="row">
              <div
                className="col-sm-12"
                style={{ marginTop: "1em", fontSize: "11px" }}
              >
                To:
              </div>
              <div className="col-sm-12">
                <TextField
                  value={toEmail}
                  error={!!toEmailError}
                  helperText={toEmailError}
                  fullWidth
                  onChange={handleToEmailChange}
                  style={{ marginTop: ".1em", fontSize: "11px" }}
                  InputProps={{
                    style: {
                      height: "3.5vh",
                    },
                  }}
                />
              </div>
              <div
                className="col-sm-12"
                style={{ marginTop: "1em", fontSize: "11px" }}
              >
                CC:
              </div>
              <div className="col-sm-12" style={{ marginTop: ".1em" }}>
                <TextField
                  id="outlined-basic"
                  variant="outlined"
                  value={ccEmail}
                  error={!!ccEmailError}
                  helperText={ccEmailError}
                  fullWidth
                  onChange={handleCCEmailChange}
                  InputProps={{
                    style: {
                      height: "3.5vh",
                      fontSize: "11px",
                    },
                  }}
                />
              </div>
              <div
                className="col-sm-12"
                style={{ marginTop: "1em", fontSize: "11px" }}
              >
                Subject:
              </div>
              <div className="col-sm-12" style={{ marginTop: ".1em" }}>
                <TextField
                  id="outlined-basic"
                  variant="outlined"
                  fullWidth
                  value={subject}
                  error={!!subjectError}
                  helperText={subjectError}
                  onChange={handleSubjectChange}
                  InputProps={{
                    style: {
                      height: "3.5vh",
                      fontSize: "11px",
                    },
                  }}
                />
              </div>
              <div
                className="col-sm-12"
                style={{ marginTop: "1em", fontSize: "11px" }}
              >
                Message:
              </div>
              <div className="col-sm-12" style={{ marginTop: ".1em" }}>
                <TextField
                  multiline
                  minRows={30}
                  aria-label="maxWidth"
                  maxRows={21}
                  style={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    fontSize: "11px",
                  }}
                  placeholder="Message"
                  value={
                    emailDocumentData !== null
                      ? "Greetings,\n\n" +
                        emailDocumentData +
                        "\n\nThe information contained in this email message is intended for the named recipients only. It may contain confidential information. If you are not the intended recipient of this message, you may not disclose, use, print, copy, or disseminate this information. If you have received this in error, please reply to and notify the sender (only) and delete this message. [In the U.S. only, add: “Unauthorized interception of this email is a violation of federal criminal law.”]"
                      : ""
                  }
                  inputProps={{
                    style: {
                      backgroundColor: "var(--aggrid-background-color)",
                      font: "menu",
                      fontFamily: "monospace",
                      fontSize: "11px",
                    },
                  }}
                  InputProps={{
                    readOnly: false,
                  }}
                />
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handelSendEmail}
            style={{ fontSize: "14px", height: "2.1em", padding: "4px 6px",border: "1px solid #144979", }}
          >
            Send
          </Button>
          <Button
            style={{ fontSize: "14px", height: "2.1em", padding: "4px 6px",border: "1px solid #144979", }}
            onClick={handleCloseClick}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => handleConfirmDialogClose(false)}
      >
        <DialogTitle
          style={{
            backgroundColor: "var(--primary-color)",
            color: "var(--aggrid-text-color)",
            fontSize: "14px",
          }}
        >
          Cancel Confirmation
        </DialogTitle>
        <DialogContent style={{ fontSize: "14px", marginTop: "20px" }}>
          Are you sure you want to Cancel?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleConfirmDialogClose(true)}
            color="primary"
            style={{
              fontSize: "14px",
              height: "2.1em",
              padding: "4px 6px",
              border: "1px solid #144979",
            }}
          >
            Yes
          </Button>
          <Button
            onClick={() => handleConfirmDialogClose(false)}
            color="primary"
            style={{
              fontSize: "14px",
              height: "2.1em",
              padding: "4px 6px",
              border: "1px solid #144979",
            }}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EmailDocument;
